import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Building2, User, Users, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CompanyLogoUpload } from "@/components/settings/CompanyLogoUpload";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  CompanyType,
  getCompanyTypeOptions,
  getCompanyTypeLabel
} from "@/types/company";

function CompanySection({
  profile,
  onCompanyChange,
  refreshKey,
  companyId,
}: {
  profile: any;
  onCompanyChange: (newCompanyId: number | null) => void;
  refreshKey: number;
  companyId: number | null;
}) {
  const [company, setCompany] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [companyType, setCompanyType] = useState<CompanyType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);

  useEffect(() => {
    async function fetchCompany() {
      if (companyId && !Number.isNaN(companyId)) {
        try {
          // Buscar usuários da empresa
          const { data: users, error: usersError } = await supabase
            .from("profiles")
            .select("id, full_name, avatar_url, member_since:updated_at")
            .eq("company_id", companyId);
          
          if (usersError) {
            console.error("Erro ao buscar usuários da empresa:", usersError);
            toast.error("Erro ao carregar membros da empresa");
            return;
          }
          
          setCompanyUsers(
            (users || []).map((user) => ({
              ...user,
              member_since: user.member_since ?? null,
            }))
          );
          
          const { data: companyData, error: companyError } = await supabase
            .from("companies")
            .select("*")
            .eq("id", companyId)
            .maybeSingle();

          if (companyError) {
            console.error("Erro ao carregar empresa:", companyError);
            toast.error("Erro ao carregar empresa");
            return;
          }

          setCompany({
            id: companyData?.id ?? companyId,
            name: companyData?.name ?? "Minha Empresa",
            logo_url: companyData?.logo_url ?? "",
            company_type: companyData?.company_type ?? null,
            member_count: users?.length || 0,
            created_at: companyData?.created_at ?? null,
            updated_at: companyData?.updated_at ?? null,
          });
          setName(companyData?.name ?? "Minha Empresa");
          setLogoUrl(companyData?.logo_url ?? "");
          setCompanyType(companyData?.company_type ?? null);
        } catch (error) {
          console.error("Erro inesperado ao buscar empresa:", error);
          toast.error("Erro inesperado ao carregar empresa");
        }
      } else {
        // Usuário não tem company_id
        setCompany(null);
        setName("");
        setLogoUrl("");
        setCompanyType(null);
        setCompanyUsers([]);
      }
    }
    fetchCompany();
  }, [companyId, profile, refreshKey]);

  async function handleSave() {
    if (!name.trim()) {
      toast.error("Nome da empresa é obrigatório");
      return;
    }

    if (!profile?.id) {
      toast.error("Perfil do usuário não identificado");
      return;
    }

    setIsLoading(true);
    try {
      const currentCompanyId = companyId && !Number.isNaN(companyId) ? companyId : null;

      if (!currentCompanyId) {
        // Descobrir próximo ID disponível (fallback simples)
        const { data: lastCompany, error: lastCompanyError } = await supabase
          .from("companies")
          .select("id")
          .order("id", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (lastCompanyError) {
          console.error("Erro ao recuperar última empresa:", lastCompanyError);
          throw new Error("Erro ao recuperar identificador da empresa");
        }

        const lastIdRaw = lastCompany?.id ?? 0;
        const lastId =
          typeof lastIdRaw === "string" ? Number(lastIdRaw) : lastIdRaw;
        const nextCompanyId = (Number.isFinite(lastId) ? lastId : 0) + 1;

        // Criar nova empresa na tabela companies
        const { data: insertedCompany, error: insertError } = await supabase
          .from("companies")
          .insert({
            id: nextCompanyId,
            name,
            company_type: companyType,
            user_id: profile?.id ?? null,
          })
          .select()
          .maybeSingle();

        if (insertError) {
          console.error("Erro ao criar empresa:", insertError);
          throw new Error("Erro ao criar empresa: " + insertError.message);
        }

        if (!insertedCompany?.id) {
          throw new Error("Não foi possível obter o ID da empresa criada.");
        }

        const newCompanyId =
          typeof insertedCompany.id === "string"
            ? Number(insertedCompany.id)
            : insertedCompany.id;

        if (!newCompanyId || Number.isNaN(newCompanyId)) {
          throw new Error("ID da empresa criado é inválido.");
        }

        const { error: profileUpdateError } = await supabase
          .from("profiles")
          .update({ company_id: newCompanyId })
          .eq("id", profile.id);
        
        if (profileUpdateError) {
          console.error("Erro ao associar empresa ao perfil:", profileUpdateError);
          throw new Error("Erro ao associar empresa ao perfil: " + profileUpdateError.message);
        }
        
        toast.success("Empresa criada e associada ao perfil!");
        setIsEditing(false);
        onCompanyChange(newCompanyId);
        setCompany({
          id: newCompanyId,
          name: insertedCompany.name ?? name,
          logo_url: insertedCompany.logo_url ?? "",
          company_type: insertedCompany.company_type ?? companyType,
          member_count: 1,
        });
        setCompanyUsers([
          {
            id: profile.id,
            full_name: profile.full_name ?? "",
            avatar_url: profile.avatar_url ?? null,
            member_since: new Date().toISOString(),
          },
        ]);
      } else {
        try {
          const { error: companyUpdateError } = await supabase
            .from("companies")
            .update({
              name,
              company_type: companyType
            })
            .eq("id", currentCompanyId);

          if (companyUpdateError) {
            throw companyUpdateError;
          }

          toast.success("Informações da empresa atualizadas!");
          setIsEditing(false);

          setCompany(prev => ({
            ...prev,
            name,
            company_type: companyType,
          }));
        } catch (updateError) {
          console.error("Erro ao atualizar empresa:", updateError);
          toast.error("Erro ao atualizar empresa");
        }
      }
    } catch (error: any) {
      console.error("Erro ao salvar empresa:", error);
      toast.error(error.message || "Erro ao salvar empresa");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleRemoveFromCompany() {
    if (!window.confirm("Tem certeza que deseja sair desta empresa? Esta ação não pode ser desfeita.")) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ company_id: null })
        .eq("id", profile.id);
      
      if (error) {
        console.error("Erro ao sair da empresa:", error);
        throw new Error("Erro ao sair da empresa: " + error.message);
      }
      
      toast.success("Você saiu da empresa!");
      setCompany(null);
      setName("");
      setLogoUrl("");
      setCompanyType(null);
      setCompanyUsers([]);
      setIsEditing(false);
      onCompanyChange(null);
    } catch (error: any) {
      console.error("Erro ao sair da empresa:", error);
      toast.error(error.message || "Erro ao sair da empresa");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {company || isEditing ? (
        <div className="space-y-6">
          {/* Company Info */}
          <div className="flex items-start gap-6 p-6 border rounded-lg bg-card">
            <CompanyLogoUpload
              companyId={company?.id || companyId || ''}
              companyName={name}
              currentLogoUrl={logoUrl}
              onLogoChange={(newUrl) => setLogoUrl(newUrl || '')}
              isEditing={isEditing}
            />
            
            <div className="flex-1 space-y-4">
              {isEditing ? (
                <>
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Nome da Empresa</label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Nome da empresa"
                      className="font-medium"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Tipo de Empresa</label>
                    <Select
                      value={companyType || undefined}
                      onValueChange={(value) => setCompanyType(value as CompanyType)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecione o tipo de empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        {getCompanyTypeOptions().map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Ajuda a personalizar categorias e relatórios para seu negócio
                    </p>
                  </div>
                </>
              ) : (
                <div>
                  <h2 className="text-2xl font-bold">{company?.name}</h2>
                  <p className="text-sm text-muted-foreground">
                    ID: {String(company?.id ?? companyId ?? "").slice(0, 8)}...
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    <span className="font-medium">Tipo:</span> {getCompanyTypeLabel(company?.company_type)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {companyUsers.length} {companyUsers.length === 1 ? 'membro' : 'membros'}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-xs text-muted-foreground">Ativa</span>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <Button onClick={handleSave} disabled={isLoading || !name}>
                      {isLoading ? "Salvando..." : "Salvar"}
                    </Button>
                    <Button variant="outline" onClick={() => setIsEditing(false)}>
                      Cancelar
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setIsEditing(true)}>
                      Editar Empresa
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleRemoveFromCompany}
                      disabled={isLoading}
                    >
                      Sair da Empresa
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-6 p-12 border-2 border-dashed border-muted rounded-lg">
          <Building2 className="h-16 w-16 text-muted-foreground" />
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2">Nenhuma empresa associada</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Crie uma nova empresa para organizar seus dados financeiros ou aguarde um convite para participar de uma empresa existente
            </p>
            <Button size="lg" onClick={() => setIsEditing(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Criar Nova Empresa
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CompanyMembersSection({ profile, refreshKey, companyId }: { profile: any; refreshKey: number; companyId: number | null }) {
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [invitePassword, setInvitePassword] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const derivedCompanyIdRaw = profile?.company_id;
  const fallbackCompanyId =
    typeof derivedCompanyIdRaw === "string"
      ? Number(derivedCompanyIdRaw)
      : derivedCompanyIdRaw ?? null;
  const effectiveCompanyId =
    companyId && !Number.isNaN(companyId)
      ? companyId
      : fallbackCompanyId && !Number.isNaN(fallbackCompanyId)
      ? fallbackCompanyId
      : null;

  useEffect(() => {
    async function fetchCompanyUsers() {
      if (!effectiveCompanyId || Number.isNaN(effectiveCompanyId)) {
        setCompanyUsers([]);
        return;
      }
      
      setIsLoading(true);
      try {
        const { data: users, error } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url, member_since:updated_at")
          .eq("company_id", effectiveCompanyId);
        
        if (error) {
          console.error("Erro ao buscar membros da empresa:", error);
          toast.error("Erro ao carregar membros da empresa");
          setCompanyUsers([]);
        } else {
          setCompanyUsers(
            (users || []).map((user) => ({
              ...user,
              member_since: user.member_since ?? null,
            }))
          );
        }
      } catch (error) {
        console.error("Erro inesperado ao buscar membros:", error);
        toast.error("Erro inesperado ao carregar membros");
        setCompanyUsers([]);
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchCompanyUsers();
  }, [effectiveCompanyId, refreshKey]);

  async function handleInviteUser() {
    if (!inviteEmail.trim() || !inviteName.trim()) {
      toast.error("Email e nome são obrigatórios");
      return;
    }

    setInviteLoading(true);
    try {
      // Verificar se o email já existe (funcionalidade simulada)
      // Em uma implementação real, você verificaria se o usuário já existe

      // Simular convite (na prática, você enviaria um email)
      toast.success(`Convite enviado para ${inviteEmail}! (Funcionalidade simulada)`);
      setInviteDialogOpen(false);
      setInviteEmail("");
      setInviteName("");
      setInvitePassword("");
    } catch (error: any) {
      console.error("Erro ao enviar convite:", error);
      toast.error("Erro ao enviar convite");
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleRemoveUser(userId: string) {
    if (!window.confirm("Tem certeza que deseja remover este usuário da empresa? Esta ação não pode ser desfeita.")) return;
    
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ company_id: null })
        .eq("id", userId);
      
      if (error) {
        console.error("Erro ao remover usuário:", error);
        throw new Error("Erro ao remover usuário: " + error.message);
      }
      
      setCompanyUsers(users => users.filter(u => u.id !== userId));
      toast.success("Usuário removido da empresa");
    } catch (error: any) {
      console.error("Erro ao remover usuário:", error);
      toast.error(error.message || "Erro ao remover usuário");
    }
  }

  if (!effectiveCompanyId || Number.isNaN(effectiveCompanyId)) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Membros da Empresa ({companyUsers.length})
          </CardTitle>
          <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Convidar Usuário
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Convidar Usuário para a Empresa</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  placeholder="Nome do usuário"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                />
                <Input
                  type="email"
                  placeholder="Email do usuário"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
                <Input
                  type="password"
                  placeholder="Senha inicial"
                  value={invitePassword}
                  onChange={(e) => setInvitePassword(e.target.value)}
                />
                <Button 
                  onClick={handleInviteUser} 
                  disabled={inviteLoading || !inviteEmail || !inviteName}
                  className="w-full"
                >
                  {inviteLoading ? "Enviando..." : "Enviar Convite"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8">Carregando membros...</div>
        ) : companyUsers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nenhum membro encontrado
          </div>
        ) : (
          <div className="space-y-3">
            {companyUsers.map((user) => (
              <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {user.full_name || "Usuário"}
                      {user.id === profile.id && (
                        <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                          Você
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {user.member_since
                        ? `Membro desde ${new Date(user.member_since).getFullYear()}`
                        : "Data de ingresso não disponível"}
                    </p>
                  </div>
                </div>
                {user.id !== profile.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveUser(user.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function CompanySettings() {
  const { profile } = useAuth();
  const [companyRefresh, setCompanyRefresh] = useState(0);
  const derivedCompanyIdRaw = profile?.company_id;
  const derivedCompanyId =
    typeof derivedCompanyIdRaw === "string"
      ? Number(derivedCompanyIdRaw)
      : derivedCompanyIdRaw ?? null;
  const normalizedCompanyId =
    derivedCompanyId && !Number.isNaN(derivedCompanyId)
      ? derivedCompanyId
      : null;
  const [companyId, setCompanyId] = useState<number | null>(normalizedCompanyId);

  useEffect(() => {
    setCompanyId((prev) => (prev !== normalizedCompanyId ? normalizedCompanyId : prev));
  }, [normalizedCompanyId]);

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configurações da Empresa</h1>
        <p className="text-muted-foreground mt-2">
          Gerencie informações da empresa e membros da equipe
        </p>
      </div>

      {/* Company Info Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Informações da Empresa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CompanySection 
            profile={profile} 
            companyId={companyId}
            onCompanyChange={(newCompanyId) => {
              setCompanyId(newCompanyId);
              setCompanyRefresh(v => v + 1);
            }} 
            refreshKey={companyRefresh}
          />
        </CardContent>
      </Card>

      {/* Company Members Section */}
      <CompanyMembersSection profile={profile} companyId={companyId} refreshKey={companyRefresh} />
    </div>
  );
} 
